import { Model, AbstractComponentController, checkClass } from "../../helpers";
import { TranslatableModel, LANGUAGES, PaginationCollection } from "../../pms-api/models";
import { UnsavedModalController } from '../components/unsaved-modal/unsaved-modal.component';

/**
 * @typedef {Object} Column
 * @property {string} name
 * @property {string} title
 * @property {boolean} sortable
 * @property {boolean} rtl
 */

/**
 * @class AbstractListComponentController
 * @template M
 */
export class AbstractListComponentController extends AbstractComponentController {

    /**
     * @protected
     * @type {function(new:M|Model)}
     */
    get modelClass() {

        return Model;
    }

    /**
     * @protected
     * @type {Column[]}
     */
    get baseColumns() {

        return [];
    }

    /**
     * @protected
     * @type {Object}
     */
    get loadParams() {

        const params = {
            page: this.pagination.page,
            perPage: this.pagination.perPage,
        };

        if (this.sort.column) {
            params[ 'orderKey' ] = this.sort.column.name.replace(/\./g, '_');
            params[ 'orderBy' ] = this.sort.desc ? 'desc' : 'asc';
        }
        
        if (this.search.query) {
            params[ 'searchValue' ] = this.search.query;
        }

        return params;
    }

    get statuses() {

        return {
            '0': {
                title: 'INACTIVE',
                css:   'inactive'
            },
            '1': {
                title: 'ACTIVE',
                css:    'active'
            }
        };
    }

    clearSearchQuery() {
        this.search.query = '';

        this.load();
    }

    getTypeaheadLoadParams(query) {

        const params = this.loadParams;

        params[ 'page' ] = 1;
        params[ 'perPage' ] = 10;
        params[ 'searchValue' ] = query;

        return params;
    }

    /**
     * @protected
     * @param {Column} column
     * @param {Language} language
     * @returns {Column}
     */
    makeTranslatableColumn(column, language) {

        return {
            name: this.modelClass.makeTranslatableAlias(column.name, language),
            title: /*language.default ? column.title : */`${column.title} (${this.$translate.instant(language.name)})`,
            sortable: column.sortable,
            rtl: language.rtl,
            width: column.width
        };
    }

    /**
     * @protected
     * @param {Column[]} columns
     * @returns {Column[]}
     */
    fillColumnsWithTranslatable(columns) {

        return columns.reduce((columns, column) => {

            if (this.modelClass.isTranslatable(column.name) && !column.noTranslate) {

                columns = columns.concat(LANGUAGES.map((language) => this.makeTranslatableColumn(column, language)));
            } else {

                columns.push(column);
            }

            return columns;
        }, []);
    }

    $onInit() {

        /**
         * @type {{column: Column|null, desc: boolean}}
         */
        this.sort = {
            column: null,
            desc: false
        };

        this.countToExport = 0;

        this.search = {
            query: ""
        };
        this.pagination = new PaginationCollection();
        this.columns = checkClass(this.modelClass, TranslatableModel) ? this.fillColumnsWithTranslatable(
            this.baseColumns) : this.baseColumns;
    }

    /**
     * @abstract
     */
    load() {

        throw new Error("Abstract method 'load' should be implemented in derived class");
    }

    /**
     * @abstract
     * @param {string} query
     * @returns {Promise}
     */
    loadForTypeahead(query) {

        throw new Error("Abstract method 'loadForTypeahead' should be implemented in derived class");
    }

    /**
     * @param {Column} column
     * @returns {string}
     */
    sortClassFor(column) {

        if (!column.sortable) {

            return;
        }

        return this.sort.column && this.sort.column.name === column.name ?
            `${(this.sort.desc ? 'desc' : 'asc')}` : '';
    }

    /**
     * @param {Column} column
     */
    sortBy(column) {

        if (!column.sortable) {

            return;
        }

        this.sort.desc = this.sort.column && this.sort.column.name === column.name ? !this.sort.desc : false;
        this.sort.column = column;

        this.load();
    }
    
    resetPaging() {
        
        this.pagination.page = 1;
        
        this.load();
    }

    /**
     * @param {M|Model=} model
     */
    openForm(model) {

        this.editable = model || new this.modelClass;
        if (!angular.isUndefined(this.editable.image)) {
            const imageSize = '150x150';
            this.editable.image_file = this.$filter('thumbnail')(this.editable.image,imageSize);

        }
    }

    /**
     * @param {M|Model} model
     */
    onFormSave(model, keep = false) {

        /*if (!this.editable.id) {
            this.pagination.models.push(model);
        } else {
            this.editable.fresh(model);
        }*/

        this.toastr.success(this.$translate.instant('YOUR_CHANGES_HAVE_BEEN_SAVED'), this.$translate.instant('SUCCESS'));

        this.clearSearchQuery();

        if (!keep) {

            this.editable = null;
        }
    }

    onFormCancel() {

        this.editable = null;
    }

    get deleteSettings() {
        return null;
    }

    /**
     * @param {M|Model=} model
     */
    openDeleteModal(model, ...params) {

       this.$uibModal.open({
            component: 'deleteModal',
            resolve: {
                model: model,
                settings: this.deleteSettings,
                params: () =>  params
            },
        }).result.then(() => {
            
            this.load();
            
            this.toastr.success(this.$translate.instant('ENTRY_HAS_BEEN_REMOVED'), this.$translate.instant('SUCCESS'));

        }).catch(() => {
            //todo handling error message
        });
    }

    checkCountExport(){

        if(this.countToExport > process.env.MAX_COUNT_EXPORT){

            this.$uibModal.open({
                component: 'warningModal',
                resolve: {
                    message: () => 'YOU_REPORT_CONTAINS_MAX_RECORD_TO_EXPORT'
                },
            });

            return false;
        }

        return true;
    }
}

/**
 * @class AbstractFormComponentController
 * @template M
 */
export class AbstractFormComponentController extends AbstractComponentController {

    static get bindings() {

        return {
            model: '<',
            onSave: '&',
            onCancel: '&'
        };
    }

    constructor(...injected) {

        super(...injected);

        this.errors = null;
        this.form = null;
        this.languages = LANGUAGES;
        this.translatableNames = {};
        this.translatableActiveTabs = {};
    }

    $onInit(ignoreEvent = false) {

        this.validationModel = this.modelClass.validationModel;

        window.scrollTo(0, 0);

        this.registerUnload();

        if (this.hasOwnProperty('$rootScope') && !ignoreEvent) {

            this.$rootScope.$emit('form-opened');
        }
    }

    $onChanges(changes) {

        this.errors = null;

        window.scrollTo(0, 0);
    }

    $onDestroy() {

        if (this.hasOwnProperty('$rootScope')) {

            this.$rootScope.$emit('form-closed');
        }
    }

    registerUnload() {

        this.$destroyWorkers.push(this.$transitions.onBefore({}, (transition) => {
            
            const state = transition.$to();
            
            if (!state.is('root.public.login') && !state.is('root.secure.access-denied')) {
                
                if (this.form && this.form.$dirty) {
                    return this.openUnsavedModal(this.form);
                }
            }
        }));

        const message = "The changes you made have not been saved";

        window.onbeforeunload = (event) => {

            if (this.form && this.form.$dirty) {

                event.returnValue = message;

                return message;
            }
        };

        this.$destroyWorkers.push(() => window.onbeforeunload = null);
    }

    /**
     * @protected
     * @type {function(new:M|Model)}
     */
    get modelClass() {

        return Model;
    }

    /**
     * @type {M|Model} model
     */
    set model(model) {

        if (this.form && this.form.$dirty) {
            this.openUnsavedModal(this.form).then(() => {
                const cls = this.modelClass;
                this._model = model instanceof cls ? model.copy() : new cls();
            });
        } else {
            const cls = this.modelClass;
            this._model = model instanceof cls ? model.copy() : new cls();
        }
    }

    /**
     * @type {M|Model} model
     */
    get model() {

        return this._model;
    }

    _save() {

        throw new Error("Abstract method 'save' should be implemented in derived class");
    }

    hasError(field, language = null) {

        field = language ? this.translatableName(field, language) : field;

        return (this.errors && this.errors[field]) || (this.form[field] && (this.form[field].$touched || this.form.$submitted) && this.form[field].$invalid);
    }

    translatableName(field, language) {

        if (!this.translatableNames[language.code]) {

            this.translatableNames[language.code] = {};
        }

        if (!this.translatableNames[language.code][field]) {

            this.translatableNames[language.code][field] = this.modelClass.makeTranslatableAlias(field, language);
        }

        return this.translatableNames[language.code][field];
    }

    cancel() {
        if (this.form && this.form.$dirty) {
            this.openUnsavedModal(this.form)
                .then(() => {
                    this.onCancel();
                });
        } else {
            this.onCancel();
        }
    }

    save(unsavedPopup = false) {

        window.scrollTo(0, 0);

        if (this.form.$invalid) {

            this.form.$setSubmitted();
            this.switchTabsLocally();

            return unsavedPopup ? Promise.reject() : false;
        }

        if (this.isSubmitting) {
            return unsavedPopup ? Promise.reject() : false;
        }

        this.isSubmitting = true;

        return this._save()
            .then((model) => {

                this.form.$setPristine();

                this.onSave(unsavedPopup ? {model: model, keep: true} : {model});
            })
            .catch((response) => {
                this.onSaveError(response);

                if (unsavedPopup) {

                    return Promise.reject();
                }
            })
            .finally(() => {
                this.isSubmitting = false;
            });
    }

    onSaveError(response) {
        //@todo need mixin to hold this functionality or even better base form component as
        // AbstractFormComponentController extends AbstractComponentController
        if (response.status === 422) {

            this.errors = response.data;

            this.switchTabs();
        } else {

            this.errors = {error: response.message};
        }
    }

    switchTabs() {

        const translatableFields = Object.keys(this.translatableActiveTabs),
            failedFields = Object.keys(this.errors);

        if (!failedFields.length || !translatableFields.length) {

            return;
        }

        translatableFields.forEach((name) => {

            for (let i = 0, language; language = this.languages[i]; i++) {

                if (failedFields.indexOf(this.translatableName(name, language)) !== -1) {

                    this.translatableActiveTabs[name] = i;
                    break;
                }
            }
        });
    }

    switchTabsLocally() {

        const translatableFields = Object.keys(this.translatableActiveTabs);

        if (!this.form || !this.form.$invalid || !translatableFields.length) {

            return;
        }

        translatableFields.forEach((name) => {

            for (let i = 0, language; language = this.languages[i]; i++) {

                if (this.hasError(name, language)) {

                    this.translatableActiveTabs[name] = i;
                    break;
                }
            }
        });
    }

    languageByCode(search) {

        return (this.languages.find(({code}) => code == search) || this.languages[0])
    }

    /**
     * @param {M|Model=} form
     */
    openUnsavedModal(form) {

        return this.$uibModal.open({
            component: 'unsavedModal',
            size: 'lg',
        }).result.then((result) => {
            if(result === UnsavedModalController.SAVE_AND_CONTINUE) {

                return this.save(true);

            } else {

                form.$setPristine();
            }
        });
    }

    get statuses() {

        return {
            '0': {
                title: 'ACTIVE',
                css:   'active'
            },
            '1': {
                title: 'INACTIVE',
                css:    'inactive'
            }
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/helpers/component.js